import { ConditionParams } from "@multiweb/clients/dataReader";
import { IAttrValue, IResultMessage, IRunMethodResult } from "@multiweb/types/contracts";
import { ISignatureTemplate } from "Global/Base/Crypto/ISignatureInfo";
import { IComparable } from "Global/Base/IComparable";
import { IDisposable } from "Global/Base/IDisposable";
import { CancellationToken } from "Global/Base/Promises/CancellationTokenSource";
import { IRightsContext } from "Global/Base/Rights/IRightsContext";
import { Event } from "../../../Global/Base/Events";
import { AttributeDescription, IDataTypeSerialization, IOWClassDescription } from "../Cache/ClassDescriptionCache/Interfaces";
import { IWorkflowMenuItem } from "../IWorkflowManager";
import { IImplicitFormInfo, RunMethodBaseParams, RunMethodWithParamsBaseParams } from "../OWManagerInterfaces";
import { SerializerParams } from "../types";
import { IValidationDataObject, IValidationDataSimple } from "./IValidationDataObject";

/**
 * Rozhraní pro parametry události, která označuje změnu hodnoty atributu
 */
export interface IChangedAttr {
    /** Název změněného atributu */
    attributeName: string;
    /** Hodnota změněného atributu */
    value: any;
}
/**
 * Rozhraní pro OWObject. Reprezentuje jeden záznam aplikačních dat.
 * Umožňuje data číst, manipulovat s nimi a provádět další operace.
 * Představuje jednu instanci těchto dat načtených ze serveru.
 * @includeToDoc
 */
export interface IOWObject extends IRightsContext, IValidationDataObject<IValidationDataSimple>, IAttrValue, IDisposable, IComparable {
    /**
     * Logický název třídy aplikačního záznamu
     */
    readonly objectName: string;
    /**
    * Lidsky čitelný identifikátor konkrétní instance aplikačního záznamu (nemusí být unikátní)
    */
     readonly objectIdentity: string;
    /**
     * Unikátní identifikátor serverové instance aplikačního záznamu
     */
     readonly instanceID: string;
    /**
     * Informace zda je aplikační záznam šifrovaný na straně serveru
     */
    readonly isServerSideEncrypted: boolean;
    /**
     * Informace zda je aplikační záznam finálně zašifrovaný (je zašifrovaný i symetrický klíč)
     */
    readonly isEncryptionFinalized: boolean;

    /**
     * Informace zda je na této instanci povoleno serverové právo na spouštění akcí
     */
    canExecuteActions: boolean;

    /**
    * Informace zda byl objekt změněn
    */
    isChanged: boolean;
    /**
    * Informace zda jde o dočasný kontejnerový objekt zneužívaný pro hromadné operace - momentálně nepoužité
    */
    isTempObject: boolean;
    /**
     * Informace zda je objekt zlikvidován
     */
    readonly isDisposed: boolean;
    /**
    * Podrobnosti o třídě objektu. Seznam atributů a jejich typů a další obecné vlastnosti konkrétní třídy
    */
    readonly classInfo: IOWClassDescription;
    /**
     * Zda je dostupné uživatelem zadané heslo pro šifrování a dešifrování tohoto aplikačního záznamu
     */
    hasEncryptionPassword: boolean;
    /**
    * Název celé vazby, ve které je umístěn tento objekt na nejvyšším rodičovském záznamu
    */
    attributeOnParentObject: string;
    /**
    * Značí, zda na objektu právě probíhá změna hodnoty
    */
    isInstanceUpdateInProgress: boolean;

    /**
     * Událost vyvolaná při nastavení hodnoty atributu
     */
    readonly valueSet: Event<IOWObject, IChangedAttr>;

    /**
     * Událost vyvolaná při změně atributu
     */
    readonly attributeChanged: Event<IOWObject, string>;
    /**
     * Událost vyvolaná zperzistentnění (prvním uložení) záznamu
     */
    readonly newSaved: Event<IOWObject, void>;
    /**
     * Událost vyvolaná před uložením záznamu
     */
    readonly beforeSave: Event<IOWObject, void>;
    /**
     * Událost vyvolaná při uložení záznamu
     */
    readonly saved: Event<IOWObject, void>;
    /**
     * Událost vyvolaná při refreshi dat objektu
     */
    readonly dataRefreshed: Event<IOWObject, boolean>;
    /**
     * Událost vyvolaná při dešifrování záznamu
     */
    readonly objectDecrypted: Event<IOWObject, undefined>;
    /**
     * Událost vyvolaná při uvolnění instance
     */
    readonly disposed: Event<IOWObject, undefined>;
    /**
     * Událost vyvolaná při smazání záznamu
     */
    readonly deleted: Event<IOWObject, undefined>;

    getSerializers(ai: AttributeDescription, params?: SerializerParams): Promise<IDataTypeSerialization>;
    /**
     * Šifrovací klíč k této instanci objektu
     * @param cancellationToken Token umožňující zrušení dotazu
     */
    getEncryptionKey(cancellationToken?: CancellationToken): Promise<CryptoKey>;
    /**
     * Zaktualizuje data této instance daty ze stejné instance v paměti aplikačního serveru
     * @param cancellationToken Viz {@link CancellationToken}
     */
    refresh(cancellationToken?: CancellationToken): Promise<void>;
    /**
     * Zaktualizuje data této instance daty z perzistentního úložiště
     * @param cancellationToken Viz {@link CancellationToken}
     */
    reload(cancellationToken?: CancellationToken): Promise<void>;
    /**
     * Čeká na doběhnutí změn dat
     */
    waitForPendingUpdates(): Promise<void>;
    /**
     * Zaregistruje probíhající změnu dat
     */
    enqueuePendingUpdateTask(task: Promise<void>): Promise<void>;
    /**
     * Dešifruje objekt a vrací informaci o tom, zda všechno proběhlo v pořádku
     * @param cancellationToken Viz {@link CancellationToken}
     */
    decrypt(cancellationToken?: CancellationToken): Promise<boolean>;
    /**
     * Vrací název třídy objektu v předané vazbě. Pokud předaný atribut není vazební, vrací null.
     *
     * @param attributeName Jméno vazebního atributu, pro který se hledá název třídy
     * @param cancellationToken Viz {@link CancellationToken}
     */
    getObjectAttributeClass(attributeName: string, cancellationToken?: CancellationToken): Promise<string | null>;
    /**
     * Vrátí metadata o atributu aplikační třídy v dané cestě
     * @param attributePath Název atributu, může obsahovat i cestu přes vazební atributy (přes tečky)
     * @param cancellationToken Viz {@link CancellationToken}
     */
    getAttributeMetadata(attributePath: string, cancellationToken?: CancellationToken): Promise<AttributeDescription | null>;
    /**
     * Provede serverovou validaci objektu. Pokud jsou nalezeny nějaké validační chyby, tak jsou vyhozeny jako validační výjimka.
     * @param cancellationToken Viz {@link CancellationToken}
     */
    validate(cancellationToken?: CancellationToken): Promise<void>;
    /**
     * Provede serverovou odlozenou validaci objektu. Pokud jsou nalezeny nějaké validační chyby, tak jsou vyhozeny jako validační výjimka.
     * @param cancellationToken Viz {@link CancellationToken}
     */
    postponedValidate(cancellationToken?: CancellationToken): Promise<void>;

    /**
     * Vrátí hodnotu atributu
     * @param attributeName Název atributu
     * @param cancellationToken Viz {@link CancellationToken}
     */
    getValue(attributeName: string, cancellationToken?: CancellationToken): Promise<IAttrValue>;
    /**
     * Vrátí seznam hodnot atributů
     * @param attrNames Pole názvu atributů
     * @param cancellationToken Viz {@link CancellationToken}
     */
    getValues(attrNames: string[], cancellationToken?: CancellationToken): Promise<IAttrValue[]>;
    /**
     * Získá parametry pro podmínku podle předaných aplikačních atributů
     * @param attrNames Pole názvu atributů
     */
    getParameters(attrNames: readonly string[]): Promise<ConditionParams>;
    /**
     * Umožňuje čekat až se vykonají všechny probíhající změny hodnot na objektu
     * @param cancellationToken Token umožňující zrušení dotazu
     */
    waitForUpdateInstance(cancellationToken?: CancellationToken): Promise<void>;
    /**
     * Nastaví hodnotu atributu.
     * @param attributeName Název atributu
     * @param value Nová hodnota atributu
     * @param cancellationToken Viz {@link CancellationToken}
     */
    setValue(attributeName: string, value: any, cancellationToken?: CancellationToken): Promise<IOWObject>;
    /**
     * Potřebuje dotáhnout. Navázat třeba na this._updatingAttrs - chtělo by to udělat obalovací metodu
     * pro operace používající this._updatingAttrs, ať se ta ptákovina nekopíruje všude.
     * @param cancellationToken Viz {@link CancellationToken}
     */
    undo(cancellationToken?: CancellationToken): Promise<void>;
    /**
     * Potřebuje dotáhnout. Navázat třeba na this._updatingAttrs - chtělo by to udělat obalovací metodu
     * pro operace používající this._updatingAttrs, ať se ta ptákovina nekopíruje všude.
     * @param cancellationToken Viz {@link CancellationToken}
     */
    redo(cancellationToken?: CancellationToken): Promise<void>;
    /**
     * Najde názvy vazeb pro hledanou instanci.
     * @param instanceID Hledaná instance
     */
    getLinkNamesForInstance(instanceID: string): Promise<string[]>;
    /**
     * Trvale odstraní aplikační záznam z perzistentního úložiště
     * @param cancellationToken Viz {@link CancellationToken}
     */
    delete(cancellationToken?: CancellationToken): Promise<IResultMessage[]>;
    /**
     * Uloží změny na aplikačním záznamu
     * @param cancellationToken Viz {@link CancellationToken}
     */
    applyUpdates(cancellationToken?: CancellationToken): Promise<IResultMessage[]>;
    /**
     * Zruší všechny probíhající změny hodnot atributů
     */
    cancelUpdatesInProgress(): void;
    /**
     * Spouští na serveru aplikační metodu (bez parametrů) nad aplikačním objektem
     *
     * @param methodName Název metody
     * @param cancellationToken Umožňuje zrušení požadavku
     * @return True - metoda na serveru doběhla, False - metoda běží na serveru na pozadí v režimu dlouhé transakce
     */
    run(config: RunMethodBaseParams, cancellationToken?: CancellationToken): Promise<IRunMethodResult>;
    /**
     * Spouští na serveru aplikační metodu (s parametry) nad aplikačním objektem
     *
     * @param methodName Název metody
     * @param params Parametry
     * @param methodLocKey Uživatelský název metody
     * @param formID
     * @param cancellationToken Umožňuje zrušení požadavku
     */
    runWithParams(config: RunMethodWithParamsBaseParams, cancellationToken?: CancellationToken): Promise<IRunMethodResult>;
    /**
     * Vytváří reprezentaci dané instance aplikačního záznamu určenou pro podepsání
     * @param template Šablona, na základě níž má být vytvořena nová reprezentace
     * @param serializationFormat Formát, který se má použít pro serializaci dat
     * @param cancellationToken Viz {@link CancellationToken}
     */
    mapSignDataToTemplate(template: ISignatureTemplate, serializationFormat: string, cancellationToken?: CancellationToken): Promise<object>;
    /**
     * Načte pole WorkFlow menu k objektu nebo k seznamu objektů pro hromadný výběr
     * @param sort Pokud je true, tak setřídí menu podle vlastnosti Order v aktivitě, ke které menu patří, jinak vrátí nesetříděné
     * @param cancellationToken Viz {@link CancellationToken}
     */
    wfMenu(sort?: boolean, cancellationToken?: CancellationToken): Promise<IWorkflowMenuItem[]>;
    /**
     * Vytvoří kopii aplikačního záznamu
     * @param cancellationToken Viz {@link CancellationToken}
     */
    createCopy(cancellationToken?: CancellationToken): Promise<IOWObject>;
    /**
     * Kontroluje, zda má objekt přiřazené nějaké WorkFlow
     * @param cancellationToken Viz {@link CancellationToken}
     */
    hasWf(cancellationToken?: CancellationToken): Promise<boolean>;
    /**
     * Vrátí ID objektu v daném atributu
     * @param attributeName Název atributu
     * @param cancellationToken Umožňuje zrušení požadavku
     */
    getObjectAttributeID(attributeName: string, cancellationToken?: CancellationToken): Promise<string>;
     /**
     * Zjišťuje jméno implicitního formuláře pro danou instanci aplikačního záznamu
     * @param attrName Jméno atributu objektu, na kterém leží OWObject, pro který je potřeba zjistit jméno implicitního formuláře
     * @param conditionParameters Seznam parametrů pro podmínku pro zjištění jména implicitního formuláře (rozdělovačem je ';')
     * @param cancellationToken Viz {@link CancellationToken}
     */
    getImplicitForm(attrName?: string, conditionParameters?: string, cancellationToken?: CancellationToken): Promise<IImplicitFormInfo | null>;
    /**
     * Zkontroluje, zda je atribut na rozhraní jen ke čtení
     * @param attrName Název atributu objektu
     * @param cancellationToken Viz {@link CancellationToken}
     */
    isAttributeReadOnly(attrName: string, cancellationToken?: CancellationToken): Promise<boolean>;
    /**
     * Zkontroluje, zda je atribut šifrovaný
     * @param attrName Název atributu objektu
     * @param cancellationToken Viz {@link CancellationToken}
     */
    isAttributeEncrypted(attrName: string, cancellationToken?: CancellationToken): Promise<boolean>;
    /**
     * Validuje předaný digitální podpis a připojuje jej k aplikačnímu záznamu
     * @param signature Digitální podpis vytvořený na klientské straně nebo null, pokud je podpis v objektu
     * @param signedData Podepisovaná data v textové podobě
     * @param profileId ID profilu s nastavením provideru
     * @param providerType Typ podpisovacího provideru
     * @param providerVersion Verze podpisovacího provideru
     * @param signatureSourcesEncrypted Příznak, jestli zdrojová data digitálního podpisu jsou zašifrovaná.
     * @param cancellationToken Viz {@link CancellationToken}
     * @returns Výsledek validace a připojení digitálního podpisu k dokumentu
     */
    createSignature(signature: string, signedData: string, profileId: string, providerType: string, providerVersion: string,
                    signatureSourcesEncrypted?: boolean, cancellationToken?: CancellationToken): Promise<string>;
}

export class IOWObject {
    private static _properties: (keyof IOWObject)[] = ["objectName", "objectIdentity", "instanceID", "isServerSideEncrypted", "isEncryptionFinalized", "canExecuteActions", "isChanged", "isTempObject", "isDisposed", "classInfo", "hasEncryptionPassword", "attributeOnParentObject", "isInstanceUpdateInProgress", "attributeChanged", "newSaved", "beforeSave", "saved", "dataRefreshed", "objectDecrypted", "disposed", "deleted"];
    private static _methods: (keyof IOWObject)[] = ["getSerializers", "getEncryptionKey", "refresh", "reload", "waitForPendingUpdates", "enqueuePendingUpdateTask", "decrypt", "getObjectAttributeClass", "validate", "postponedValidate", "getValue", "getValues", "getParameters", "waitForUpdateInstance", "setValue", "undo", "redo", "getLinkNamesForInstance", "delete", "applyUpdates", "cancelUpdatesInProgress", "run", "runWithParams", "mapSignDataToTemplate", "wfMenu", "createCopy", "hasWf", "getObjectAttributeID", "getImplicitForm", "isAttributeReadOnly", "isAttributeEncrypted", "createSignature"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && typeof instance === "object" && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function") &&
            instance instanceof IValidationDataObject &&
            instance instanceof IRightsContext &&
            instance instanceof IAttrValue &&
            instance instanceof IDisposable &&
            instance instanceof IComparable;
    }
}
